import { type RewardsData } from "@/hooks/useRewards";

interface RewardsHeroProps {
  rewards: RewardsData;
  userName: string;
}

const tierGradients: Record<string, string> = {
  bronze:   "from-amber-700/30 via-amber-700/10 to-transparent",
  silver:   "from-gray-400/30 via-gray-400/10 to-transparent",
  gold:     "from-amber-400/30 via-amber-400/10 to-transparent",
  platinum: "from-cyan-300/30 via-cyan-300/10 to-transparent",
};

const tierBarColors: Record<string, string> = {
  bronze:   "bg-amber-700",
  silver:   "bg-gray-300",
  gold:     "bg-amber-400",
  platinum: "bg-cyan-300",
};

const tierTopBars: Record<string, string> = {
  bronze:   "from-amber-700/0 via-amber-700/80 to-amber-700/0",
  silver:   "from-gray-300/0 via-gray-300/80 to-gray-300/0",
  gold:     "from-amber-400/0 via-amber-400/80 to-amber-400/0",
  platinum: "from-cyan-300/0 via-cyan-300/80 to-cyan-300/0",
};

const tierBgGlow: Record<string, string> = {
  bronze:   "bg-amber-700/10",
  silver:   "bg-gray-400/10",
  gold:     "bg-amber-400/10",
  platinum: "bg-cyan-300/10",
};

export default function RewardsHero({ rewards, userName }: RewardsHeroProps) {
  return (
    <div className={`relative bg-gradient-to-br ${tierGradients[rewards.tier]} border ${rewards.tierBorder} rounded-3xl p-7 overflow-hidden`}>
      {/* Top accent bar */}
      <div className={`absolute top-0 left-0 w-full h-0.5 bg-gradient-to-r ${tierTopBars[rewards.tier]}`}></div>

      {/* Background glow orb */}
      <div className={`absolute -top-10 -left-10 w-48 h-48 ${tierBgGlow[rewards.tier]} rounded-full blur-3xl pointer-events-none`}></div>

      {/* Header row */}
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <p className="text-gray-400 text-sm mb-1" style={{ fontFamily: "'Cairo', sans-serif" }}>
            مرحباً، {userName}
          </p>
          <h1 className="text-white text-2xl font-black leading-tight" style={{ fontFamily: "'Cairo', sans-serif" }}>
            نقاط المكافآت
          </h1>
        </div>
        <div className={`flex items-center gap-2 ${rewards.tierBg} border ${rewards.tierBorder} rounded-2xl px-4 py-2.5`}>
          <div className="w-8 h-8 flex items-center justify-center">
            <i className={`${rewards.tierIcon} ${rewards.tierColor} text-xl`}></i>
          </div>
          <div>
            <p className="text-gray-500 text-[10px]" style={{ fontFamily: "'Cairo', sans-serif" }}>مستواك</p>
            <p className={`${rewards.tierColor} text-sm font-black`} style={{ fontFamily: "'Cairo', sans-serif" }}>
              {rewards.tierLabel}
            </p>
          </div>
        </div>
      </div>

      {/* Points display */}
      <div className="flex items-end gap-3 mb-6">
        <p className={`text-6xl font-black ${rewards.tierColor} leading-none`} style={{ fontFamily: "'Cairo', sans-serif" }}>
          {rewards.points.toLocaleString()}
        </p>
        <div className="pb-1">
          <p className="text-gray-300 text-base font-bold" style={{ fontFamily: "'Cairo', sans-serif" }}>نقطة</p>
          <p className="text-gray-600 text-xs" style={{ fontFamily: "'Cairo', sans-serif" }}>
            إجمالي مكتسب: {rewards.totalPointsEarned.toLocaleString()}
          </p>
        </div>
      </div>

      {/* Progress bar */}
      {rewards.nextTierPoints !== null ? (
        <div>
          <div className="flex justify-between items-center mb-2">
            <span className="text-gray-400 text-xs" style={{ fontFamily: "'Cairo', sans-serif" }}>
              <span className={`font-black ${rewards.tierColor}`}>{rewards.nextTierPoints}</span> نقطة للوصول إلى{" "}
              <span className={`font-black ${rewards.tierColor}`}>{rewards.nextTierLabel}</span>
            </span>
            <span className={`text-xs font-black ${rewards.tierColor}`} style={{ fontFamily: "'Cairo', sans-serif" }}>
              {rewards.progressPercent}%
            </span>
          </div>
          <div className="h-3 bg-white/8 rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-1000 ${tierBarColors[rewards.tier]}`}
              style={{ width: `${rewards.progressPercent}%` }}
            ></div>
          </div>
        </div>
      ) : (
        <div className={`flex items-center gap-3 ${rewards.tierBg} border ${rewards.tierBorder} rounded-2xl px-4 py-3`}>
          <i className={`ri-vip-diamond-line ${rewards.tierColor} text-lg`}></i>
          <div>
            <p className={`${rewards.tierColor} text-sm font-black`} style={{ fontFamily: "'Cairo', sans-serif" }}>
              أنت في أعلى مستوى — بلاتيني مميز!
            </p>
            <p className="text-cyan-300/60 text-xs" style={{ fontFamily: "'Cairo', sans-serif" }}>
              تستمتع بجميع المزايا الحصرية
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
